/* ============================================================
   SlaBadge — TASK-243 (G6): SLA/overdue indicator for the
   current order stage. Shared by customer OrdersPage and the
   provider order queue. Light theme, Montserrat, iOS-18
   minimal per DESIGN-SYSTEM.md — muted tones, no neon.
   TASK-255: the stage word comes from the pure slaStageLabel —
   pickup/ПВЗ orders (G8-D1 snapshot passed by the caller) read
   «выдача» instead of «доставка»; legacy rows (no snapshot → Courier
   default) keep the courier word byte-identical.
   TASK-256 (§92): every caller now passes the snapshot — customer
   list rows (OrderSummary) and admin rows (AdminOrder) included.
   ============================================================ */

import type { OrderSla, WebDeliveryMethod } from '../types/product';
import { slaStageLabel } from './slaBadgeView';

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
  return 'дней';
}

/**
 * Renders nothing for OnTrack stages (keep UI calm); amber hint for
 * DueSoon; red badge + human copy for Overdue. The optional method
 * snapshot makes the Delivery stage word follow the handoff kind.
 */
export function SlaBadge({
  sla,
  deliveryMethod,
}: {
  sla: OrderSla | null | undefined;
  deliveryMethod?: WebDeliveryMethod | number | null;
}) {
  if (!sla || sla.state === 'OnTrack') return null;

  const stage = slaStageLabel(sla.stage, deliveryMethod);
  const deadline = new Date(sla.deadlineUtc).toLocaleDateString('ru-RU');

  if (sla.state === 'DueSoon') {
    return (
      <span
        className="badge badge-warning"
        title={`Срок: ${deadline}`}
        style={{ fontSize: 'var(--fs-caption)' }}
      >
        Скоро срок · {stage}
      </span>
    );
  }

  const days = sla.daysOverdue;
  const overdueText = days > 0 ? ` · ${days} ${pluralDays(days)}` : '';
  return (
    <span
      className="badge badge-danger"
      title={`Просрочено с ${deadline}`}
      style={{ fontSize: 'var(--fs-caption)' }}
    >
      ⏰ Просрочено · {stage}{overdueText}
    </span>
  );
}
